/* Common Imports */

import { styled, useTheme } from "@mui/system";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { setCurrentChatThunk } from "../../../../redux-magic/store";
import { ProfilePicture } from "../../../../redux-magic/sub-interfaces/business-profile-interface";
import {
	LastMessageInterface,
	NotificationSettingInterface,
	Participants,
	UnreadMessagesCountUser,
} from "../../../../redux-magic/sub-interfaces/chat-interface";

/* Component Imports */

import { Typography } from "@mui/material";

/* Function Imports */

/* Styled Components */

const DivColumn = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.25rem",
}));

const HeadContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	gap: "1rem",
}));

const TitleCard = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: 500,
	lineHeight: "1.5rem",
}));

const ChatCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "space-between",
	width: "100%",
	padding: "0rem",
	cursor: "pointer",
	// gap: "1rem",
}));

const CustomImg = styled("img")(({ theme }) => ({
	borderRadius: "0.5rem",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "3rem",
	width: "3rem",
	[theme.breakpoints.up("md1190")]: {
		height: "3rem",
		width: "3rem",
	},
}));

const Text = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: 300,
	lineHeight: "1rem",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
}));

const DayContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-end",
	gap: "0.5rem",
}));

const DaysAgo = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	fontSize: "0.875rem",
	fontWeight: 400,
	lineHeight: "1rem",
	gap: "0.5rem",
}));

const UnreadMessagesCount = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	fontWeight: 500,
	fontSize: "0.875rem",
	minWidth: "1.5rem",
	height: "1.5rem",
	color: "white",
	background: theme.palette.primary.main,
	borderRadius: "50%",
	padding: "0.25rem",
	textAlign: "center",
	lineHeight: 1,
}));

const ChatListCard = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_id,
	chat_type,
	chat_title,
	chat_description,
	chat_dp,
	chat_notification_settings,
	chat_participants,
	chat_last_message,
	chat_created_at,
	chat_loading,
	chat_is_broadcast,
	chat_unread_messages_count,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_id: ObjectId | string;
	chat_type: string;
	chat_title: string;
	chat_description: string;
	chat_dp: ProfilePicture;
	chat_notification_settings: NotificationSettingInterface;
	chat_participants: Array<Participants>;
	chat_last_message: LastMessageInterface;
	chat_created_at: string;
	chat_loading: boolean;
	chat_is_broadcast: boolean;
	chat_unread_messages_count: UnreadMessagesCountUser;
}) => {
	const theme = useTheme();

	/* Translations */

	const localLang = {
		now: router.locale === "ar-AE" ? "الآن" : router.locale === "kn-IN" ? "ಈಗ" : "Now",
		yesterday: router.locale === "ar-AE" ? "أمس" : router.locale === "kn-IN" ? "ನಿನ್ನೆ" : "Yesterday",
	};

	/* Chat receiver */

	const private_chat_receiver: Participants = [...chat_participants].splice(
		chat_participants.findIndex((participant: any) =>
			profile_context.is_business_profile
				? participant.business_profile_id?._id !== profile_context.business_profile_id
				: participant.user_id?._id !== (session ? (session.user ? session.user.dbUser._id : "") : ""),
		),
		1,
	)[0];

	/* Hhelper function before the ChatListCard component */

	const getFormattedDateTime = (date: Date): string => {
		const now = new Date();
		const messageDate = new Date(date);
		const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));
		const diffInHours = Math.floor(diffInMinutes / 60);
		const diffInDays = Math.floor(diffInHours / 24);

		// If less than 1 minute ago
		if (diffInMinutes < 1) {
			return localLang.now;
		}

		// If today, show time
		if (messageDate.toDateString() === now.toDateString()) {
			return messageDate.toLocaleTimeString(router.locale, {
				hour: "2-digit",
				minute: "2-digit",
				hour12: true,
			});
		}

		// If yesterday
		const yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		if (messageDate.toDateString() === yesterday.toDateString()) {
			return localLang.yesterday;
		}

		// If within last 7 days, show day name
		if (diffInDays < 7) {
			return messageDate.toLocaleDateString(router.locale, { weekday: "long" });
		}

		// Otherwise show date
		return messageDate.toLocaleDateString(router.locale, {
			day: "numeric",
			month: "short",
			year: messageDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
		});
	};

	/* Handle Click chat */

	const handleClickChat = () => {
		dispatch(
			setCurrentChatThunk({
				chat_id: chat_id,
				chat_type: chat_type,
				chat_title: chat_title,
				chat_description: chat_description,
				chat_dp: chat_dp,
				chat_is_broadcast: chat_is_broadcast,
				chat_notification_settings: chat_notification_settings,
				chat_participants: chat_participants,
				chat_created_at: chat_created_at,
			}),
		);
	};

	const chat_name: string =
		chat_type === "direct"
			? private_chat_receiver.is_business_profile === true
				? typeof private_chat_receiver.business_profile_id === "object" &&
					"title" in private_chat_receiver.business_profile_id
					? (private_chat_receiver.business_profile_id.title as string)
					: ""
				: typeof private_chat_receiver.user_id === "object" &&
					  "first_name" in private_chat_receiver.user_id &&
					  "last_name" in private_chat_receiver.user_id
					? (private_chat_receiver.user_id.first_name as string) +
						" " +
						(private_chat_receiver.user_id.last_name as string)
					: ""
			: chat_title;

	const chat_dp_url: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"display_picture_url" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.display_picture_url.file_id as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" && "profile_picture" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id.profile_picture.file_id as string)
				: "";

	const profilePicture =
		typeof chat_dp_url === "string" && chat_dp_url.includes("/images/icons/Avatar.svg")
			? "/images/icons/Avatar.svg"
			: chat_is_broadcast
				? chat_dp_url
				: `/profile-picture/${chat_dp_url}`;

	// if (chat_loading) {
	// 	// TODO: Skeleton
	// 	return <div>loading</div>;
	// }

	return (
		<ChatCard onClick={handleClickChat}>
			<HeadContainer>
				{/* Profile Picture */}

				<CustomImg
					src={profilePicture as string}
					alt={chat_name}
				/>

				<DivColumn>
					<TitleCard
						variant="body1"
						sx={{
							fontWeight: chat_unread_messages_count.unread_messages_count > 0 ? 500 : 400,
						}}
					>
						{chat_name.length > 20 ? chat_name.substring(0, 20) + "..." : chat_name}
					</TitleCard>
					<Text
						variant="body2"
						sx={{
							fontWeight: chat_unread_messages_count.unread_messages_count > 0 ? 500 : 400,
							color:
								chat_unread_messages_count.unread_messages_count > 0
									? theme.palette.text.primary
									: theme.palette.text.secondary,
						}}
					>
						{chat_last_message.body.length > 25
							? `${chat_last_message.body.substring(0, 25)}...`
							: chat_last_message.body}
					</Text>
				</DivColumn>
			</HeadContainer>

			<DayContainer>
				<DaysAgo
					sx={{
						color:
							chat_unread_messages_count.unread_messages_count > 0
								? theme.palette.primary.main
								: theme.palette.text.secondary,
					}}
				>
					{getFormattedDateTime(new Date(chat_last_message.sent_at))}
				</DaysAgo>

				{chat_unread_messages_count.unread_messages_count > 0 ? (
					<UnreadMessagesCount variant="body2">{chat_unread_messages_count.unread_messages_count}</UnreadMessagesCount>
				) : null}
			</DayContainer>
		</ChatCard>
	);
};

export default ChatListCard;
